import React, { useReducer, useState, useContext } from 'react';
import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Button,
  TextField,
  Typography,
  Divider,
  IconButton,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  GroundTruthNumberRange,
  GTNumberRangePayload,
  PartialBy,
  QuestionTypeV2,
} from '../../redux/types';
import { realNumberRegex, realNumberRegexTwoDec } from '../../utilities/regex';
import ColorContext from '../../context/ColorContext';
import GTResetButton from './GTResetButton';

const PREFIX = 'GroundTruthNumberRangeScoreEditor';

const classes = {
  numField: `${PREFIX}-numField`,
  scoreField: `${PREFIX}-scoreField`,
};

const Root = styled('div')(() => ({
  [`& .${classes.numField}`]: {
    width: '80px',
    margin: '5px 10px',
  },

  [`& .${classes.scoreField}`]: {
    width: 180,
  },
}));

type GTPayloadActions = { type: 'addCorrectRange' } | { type: 'updateCorrectRange', payload: { rangeBuffer: [string, string], index: number } }
  | { type: 'addAcceptableRange' } | { type: 'updateAcceptableRange', payload: { rangeBuffer: [string, string], index: number } }
  | { type: 'addCorrectScore', payload: number } | { type: 'addAcceptableScore', payload: number }
  | { type: 'deleteCorrectRange', payload: number } | { type: 'deleteAcceptableRange', payload: number }
  | { type: 'resetState' };

type ExtendedStatePayload = {
  correct: {
    score: number;
    rangeBuffer: [string, string][];
  }
  acceptable: {
    score: number;
    rangeBuffer: [string, string][];
  }
}

const initialState: ExtendedStatePayload = {
  correct: {
    score: 0,
    rangeBuffer: [],
  },
  acceptable: {
    score: 0,
    rangeBuffer: [],
  },
};

function reducer(
  state: ExtendedStatePayload, action: GTPayloadActions,
): ExtendedStatePayload {
  switch (action.type) {
    case 'addCorrectScore':
      return {
        ...state,
        correct: {
          ...state.correct,
          score: action.payload,
        },
      };
    case 'addAcceptableScore':
      return {
        ...state,
        acceptable: {
          ...state.acceptable,
          score: action.payload,
        },
      };
    case 'addCorrectRange':
      return {
        ...state,
        correct: {
          ...state.correct,
          rangeBuffer: state.correct.rangeBuffer.concat([['', '']]),
        },
      } as ExtendedStatePayload;
    case 'addAcceptableRange':
      return {
        ...state,
        acceptable: {
          ...state.acceptable,
          rangeBuffer: state.acceptable.rangeBuffer.concat([['', '']]),
        },
      } as ExtendedStatePayload;
    case 'updateCorrectRange':
      return {
        ...state,
        correct: {
          ...state.correct,
          rangeBuffer: state.correct.rangeBuffer.map((rng, indx) => {
            if (action.payload.index === indx) {
              return action.payload.rangeBuffer;
            }
            return rng;
          }),
        },
      };
    case 'updateAcceptableRange':
      return {
        ...state,
        acceptable: {
          ...state.acceptable,
          rangeBuffer: state.acceptable.rangeBuffer.map((rng, indx) => {
            if (action.payload.index === indx) {
              return action.payload.rangeBuffer;
            }
            return rng;
          }),
        },
      };
    case 'deleteCorrectRange':
      return {
        ...state,
        correct: {
          ...state.correct,
          rangeBuffer: state.correct.rangeBuffer.filter((range, index) => index !== action.payload),
        },
      };
    case 'deleteAcceptableRange':
      return {
        ...state,
        acceptable: {
          ...state.acceptable,
          rangeBuffer: state.acceptable
            .rangeBuffer.filter((range, index) => index !== action.payload),
        },
      };
    case 'resetState':
      return initialState;
    default:
      return state;
  }
}

/**
 * Convert GTNumberRangePayload to ExtendedStatePayload
 * @param gtPayload GTNumberRangePayload
 * @returns ExtendedStatePayload
 */
const getExtendedGTNumberRangePayload = (
  gtPayload?: GTNumberRangePayload,
): ExtendedStatePayload | undefined => {
  if (!gtPayload) {
    return undefined;
  }
  const newPayload: ExtendedStatePayload = {
    ...gtPayload,
    correct: {
      ...gtPayload.correct,
      rangeBuffer: gtPayload.correct.range.map((rng) => (
        [rng[0].toString(), rng[1].toString()]
      )),
    },
    acceptable: {
      ...gtPayload.acceptable,
      rangeBuffer: gtPayload.acceptable.range.map((rng) => (
        [rng[0].toString(), rng[1].toString()]
      )),
    },
  };
  return newPayload;
};

/**
 * Convert ExtendedStatePayload to GTNumberRangePayload
 * @param obj ExtendedStatePayload
 * @returns GTNumberRangePayload
 */
const stateToGTNumberRangePayload = (obj: ExtendedStatePayload): GTNumberRangePayload => {
  const newObj: GTNumberRangePayload = {
    correct: {
      score: obj.correct.score,
      range: obj.correct.rangeBuffer.map((r) => ([Number(r[0]), Number(r[1])])),
    },
    acceptable: {
      score: obj.acceptable.score,
      range: obj.acceptable.rangeBuffer.map((r) => ([Number(r[0]), Number(r[1])])),
    },
  };
  return newObj;
};

type Props = {
  digitalSurveyId: number;
  digitalSectionId: number;
  digitalCaseId: number;
  digitalQuestionId: number;
  digitalGroundTruthId?: number;
  digitalGTPayload?: GTNumberRangePayload;
  gtGroupId: number|null;
  onSave?: (data: PartialBy<GroundTruthNumberRange, 'id'>) => void;
  onCancel?: () => void;
  onDelete: (dgtId: number) => void;
}

const GTNumberRangeScoreEditor: React.FC<Props> = ({
  digitalSurveyId,
  digitalSectionId,
  digitalCaseId,
  digitalQuestionId,
  digitalGroundTruthId,
  digitalGTPayload,
  gtGroupId,
  onSave: onSaveProp,
  onCancel: onCancelProp,
  onDelete: onDeleteProp,
}: Props) => {
  const color = useContext(ColorContext);
  const [state, dispatch] = useReducer(
    reducer,
    getExtendedGTNumberRangePayload(digitalGTPayload) || initialState,
  );
  const [rangeError, showRangeError] = useState<boolean>(false);
  const [numberError, showNumberError] = useState<boolean>(false);

  const onCorrectScoreChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const score = parseInt(event.target.value, 10);
    dispatch({ type: 'addCorrectScore', payload: Number.isNaN(score) ? 0 : score });
  };

  const onAcceptableScoreChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const correctScore = state.correct.score;
    const score = parseInt(event.target.value, 10);
    dispatch({ type: 'addAcceptableScore', payload: Number.isNaN(score) ? 0 : Math.min(correctScore, score) });
  };

  const onAddCorrectRange = () => { dispatch({ type: 'addCorrectRange' }); };

  const onAddAcceptableRange = () => { dispatch({ type: 'addAcceptableRange' }); };

  const setRangeValue = (
    value: string,
    index: number,
    scoreType: keyof Pick<ExtendedStatePayload, 'correct' | 'acceptable'>,
    limit: 'max' | 'min',
  ) => {
    const existingRange = state[scoreType].rangeBuffer[index];
    let newRangeBuffer: [string, string];
    if (limit === 'max') {
      newRangeBuffer = [existingRange[0], value];
    } else if (limit === 'min') {
      newRangeBuffer = [value, existingRange[1]];
    } else {
      throw new Error('limit neither max nor min');
    }
    if (scoreType === 'correct') {
      dispatch({
        type: 'updateCorrectRange',
        payload: {
          rangeBuffer: newRangeBuffer,
          index,
        },
      });
    }
    if (scoreType === 'acceptable') {
      dispatch({
        type: 'updateAcceptableRange',
        payload: {
          rangeBuffer: newRangeBuffer,
          index,
        },
      });
    }
  };

  const deleteRange = (rangeIndex: number, scoreType: keyof Pick<GTNumberRangePayload, 'correct' | 'acceptable'>) => {
    if (scoreType === 'correct') {
      dispatch({
        type: 'deleteCorrectRange',
        payload: rangeIndex,
      });
    }
    if (scoreType === 'acceptable') {
      dispatch({
        type: 'deleteAcceptableRange',
        payload: rangeIndex,
      });
    }
  };

  const onCancel = () => {
    dispatch({ type: 'resetState' });
    showRangeError(false);
    showNumberError(false);
    if (onCancelProp) {
      onCancelProp();
    }
  };

  const numbersAreValid = () => {
    const validCorrect = state.correct.rangeBuffer.every((rng) => {
      const valid = realNumberRegexTwoDec.test(rng[0]) && realNumberRegexTwoDec.test(rng[1]);
      return valid;
    });
    const validAcceptable = state.acceptable.rangeBuffer.every((rng) => {
      const valid = realNumberRegexTwoDec.test(rng[0]) && realNumberRegexTwoDec.test(rng[1]);
      return valid;
    });
    return validCorrect && validAcceptable;
  };

  const validateScores = (payload: GTNumberRangePayload): boolean => {
    const correctSatisfied = payload.correct.range.every((range) => range[0] <= range[1]);
    const acceptableSatisfied = payload.acceptable.range.every((range) => range[0] <= range[1]);
    return (correctSatisfied && acceptableSatisfied);
  };

  const onSave = (): void => {
    const validNumbers = numbersAreValid();
    if (!validNumbers) {
      showNumberError(true);
      return;
    }

    const gtNumRangePayload = stateToGTNumberRangePayload(state);

    const validScores = validateScores(gtNumRangePayload);
    if (!validScores) {
      showRangeError(true);
      return;
    }

    const gtScoreData: PartialBy<GroundTruthNumberRange, 'id'> = {
      id: digitalGroundTruthId,
      digitalSurveyId,
      digitalSectionId,
      digitalQuestionId,
      digitalCaseId,
      payload: gtNumRangePayload,
      type: QuestionTypeV2.NumberRange,
      digitalGroundTruthGroupId: gtGroupId,
    };
    showRangeError(false);
    showNumberError(false);
    if (onSaveProp) {
      onSaveProp(gtScoreData);
    }
  };

  return (
    <Root>
      <Box display="flex" justifyContent="space-evenly">
        {/* Correct score box */}
        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography>Correct Score</Typography>
          <div className={`mt3 mb3 ${classes.scoreField}`}>
            <TextField label="Correct Score" type="number" fullWidth variant="standard" size="small" value={state.correct.score} onChange={onCorrectScoreChange} />
          </div>
          <div className="mt2 mb2">
            {
              state.correct.rangeBuffer.map((val, indx) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={indx}>
                  <TextField
                    className={classes.numField}
                    label="Min"
                    value={val[0]}
                    variant="outlined"
                    size="small"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setRangeValue(e.target.value, indx, 'correct', 'min'); }}
                  />
                  <TextField
                    className={classes.numField}
                    label="Max"
                    value={val[1]}
                    variant="outlined"
                    size="small"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setRangeValue(e.target.value, indx, 'correct', 'max'); }}
                  />
                  <IconButton size="small" onClick={() => { deleteRange(indx, 'correct'); }} style={{ marginTop: 8 }}><CloseIcon /></IconButton>
                </div>
              ))
            }
          </div>
          <Button variant="outlined" color={color} onClick={onAddCorrectRange}>Add Range</Button>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Acceptable score box */}
        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography>Acceptable Score</Typography>
          <div className={`mt3 mb3 ${classes.scoreField}`}>
            <TextField label="Acceptable Score" type="number" fullWidth variant="standard" size="small" value={state.acceptable.score} onChange={onAcceptableScoreChange} />
          </div>
          <div className="mt2 mb2">
            {
              state.acceptable.rangeBuffer.map((val, indx) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={indx}>
                  <TextField
                    className={classes.numField}
                    label="Min"
                    value={val[0]}
                    variant="outlined"
                    size="small"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setRangeValue(e.target.value, indx, 'acceptable', 'min'); }}
                  />
                  <TextField
                    className={classes.numField}
                    label="Max"
                    value={val[1]}
                    variant="outlined"
                    size="small"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setRangeValue(e.target.value, indx, 'acceptable', 'max'); }}
                  />
                  <IconButton size="small" onClick={() => { deleteRange(indx, 'acceptable'); }} style={{ marginTop: 8 }}><CloseIcon /></IconButton>
                </div>
              ))
            }
          </div>
          <Button variant="outlined" color={color} onClick={onAddAcceptableRange}>Add Range</Button>
        </Box>
      </Box>
      <Divider style={{ marginTop: 30 }} />
      {
        rangeError && (
          <div className="mt2">
            <Alert severity="error">
              <FormattedMessage id="DRT.gtRangeError" />
            </Alert>
          </div>
        )
      }
      {
        numberError && (
          <div className="mt2">
            <Alert severity="error">
              <FormattedMessage id="DRT.gtNumberError" />
            </Alert>
          </div>
        )
      }
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }} gap={1}>
        <Box flexGrow={1}>
          <GTResetButton groundTruthId={digitalGroundTruthId} onReset={onDeleteProp} />
        </Box>
        <Button variant="contained" color={color} onClick={onSave}><FormattedMessage id="save" /></Button>
        <Button variant="contained" color="secondary" onClick={onCancel}><FormattedMessage id="genericCancel" /></Button>
      </Box>
    </Root>
  );
};

export default GTNumberRangeScoreEditor;
